<template>
    <b-modal
      v-model="data.showModal"
      size="lg"
      button-size="sm"
      modal-class="template-modal"
      title-class="w-full"
      footer-class="w-full"
      no-fade
      @show="onShow"
      @hide="onHide"
      scrollable
    >
      <!-- Modal header -->
      <div slot="modal-title">
      <div class="flex w-full justify-between">
        <div>SDS Products</div>
        <div>
          <div v-if="savingCount > 0" class="flex gap-2 items-center">
            <b-spinner label="Spinning" small></b-spinner>
            <span>Saving..</span>
          </div>
          <div v-else-if="!canSave" class="font-bold text-red-500">
            Changes won't be saved until fixed.
          </div>
          <div v-else-if="error" class="font-bold text-red-500">
            {{ error }}
          </div>
          <div v-else>
            Changes are saved automatically.
          </div>
        </div>
      </div>
    </div>

      <!-- Modal content -->
      <div class="flex flex-col gap-2">
        <div v-if="isLoading && sds === null">
          Loading..
        </div>

        <table v-if="sds" class="table table-google table-highlight">
          <thead>
            <tr>
              <th></th>
              <th>Product</th>
              <th>Grams</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item,index) in sds"
              :key="`sds_${index}`"
              :class="{ 'copied': item.copied }"
            >
              <td class="text-nowrap">{{ item.title }}</td>
              <td class="text-nowrap">{{ item.product }}</td>
              <td class="text-right">
                <span class="text-nowrap">{{ roundNumber(item.grams, 5, 5) }}</span>
              </td>
              <td class="text-right">
                <b-button
                  variant="link"
                  @click="copyPercentage(item)"
                >
                  <span class="text-nowrap">{{ roundNumber(item.pct, 5, 5) }}</span>
                </b-button>
              </td>
            </tr>
            <tr>
              <td colspan="2"></td>
              <td class="text-right">
                <span class="font-weight-bold">
                  {{ roundNumber(totalGrams, 0, 0) }}
                </span>
              </td>
              <td class="text-right py-2">
                <span class="font-weight-bold total-percentage">
                  {{ roundNumber(totalPercentage, 0, 0) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Modal footer -->
      <div slot="modal-footer" class="w-full d-flex gap-4 justify-content-between items-center">
        <div v-if="candle" class="flex w-full justify-content-between">
          <div>
            <div class="flex gap-2 items-center">
              <div :class="{ 'candle-issue': candleIssues.includes('sds_date') }">
                SDS
              </div>
              <date-picker
                class="date-picker"
                v-model="candle.sds_date"
                format="DD.MM.YYYY"
                type="date"
                valueType="YYYY-MM-DD"
                :clearable="true"
                :lang="datepickerLocale"
                @change="onSdsDate"
              >
              </date-picker>
            </div>
          </div>

          <div>
            <div class="flex gap-2 items-center">
              <div :class="{ 'candle-issue': candleIssues.includes('product_ufi') }">
                UFI
              </div>
              <b-form-input
                v-model="candle.product_ufi"
                size="sm"
                autocomplete="off"
                :disabled="candle.sds_date === null || noProductUfi"
              ></b-form-input>

              <b-form-checkbox
                v-model="noProductUfi"
                @change="toggleNoProductUfi"
                class="text-nowrap"
                :disabled="candle.sds_date === null"
              >
                No UFI
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </template>

<script>
import _ from 'lodash';
import {
  datepickerLocale,
  roundNumber,
} from '@/helpers';
import { getCandleIssues, isCandleValid } from '@/composables/candle';

export default {
  name: 'SdsModal',
  components: {},
  props: {
    data: Object,
    id: {
      type: Number,
      required: true,
    },
  },
  computed: {
    isLoading() {
      return this.loadingCount > 0;
    },
    candleIssues() {
      return getCandleIssues(this.candle);
    },
    canSave() {
      return isCandleValid(this.candle);
    },
    totalGrams() {
      if (this.sds === null) {
        return 0;
      }
      return this.sds.reduce((acc, item) => (acc + item.grams), 0);
    },
    totalPercentage() {
      if (this.sds === null) {
        return 0;
      }
      return this.sds.reduce((acc, item) => (acc + item.pct), 0);
    },
    datepickerLocale() {
      return datepickerLocale;
    },
    preventClose() {
      return !this.canSave || this.error !== null;
    },
  },
  data() {
    return {
      savingCount: 0,
      loadingCount: 0,
      filters: {},
      options: {},
      sds: null,
      candle: null,
      error: null,
      noProductUfi: false,
      scheduleSaving: _.debounce(() => {
        this.saveCandle();
      }, 250),
    };
  },
  methods: {
    processTitle() {
      const colorantTexts = this.info.fragrance_text.split(' - ');
      const colorantText = colorantTexts.length > 1 ? colorantTexts[1] : colorantTexts[0];
      const candleBrand = this.info.candle_brand_text;
      this.title = `${candleBrand} - ${colorantText}`;
    },
    fetchCandle() {
      this.loadingCount++;
      this.$http
        .get(`/candle/${this.id}`)
        .then((res) => {
          this.candle = res.body;
          if (this.candle.product_ufi === 'None') {
            this.noProductUfi = true;
          }
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch candle: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    saveCandle() {
      if (!this.canSave || !this.candle) {
        return;
      }
      this.savingCount++;
      this.loadingCount++;
      this.$http
        .put(`/candle/${this.id}`)
        .send({ changes: this.candle })
        .then((res) => {
          this.error = null;
          this.$emit('updated', res.body);
        })
        .catch((err) => {
          this.error = err.response.text;
        })
        .finally(() => {
          this.savingCount--;
          this.loadingCount--;
        });
    },
    fetchSds() {
      this.loadingCount++;
      this.$http
        .get(`/candle/${this.id}/sds`)
        .then((res) => {
          this.sds = res.body.map((item) => {
            item.copied = false;
            return item;
          });
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch SDS: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchAll() {
      this.fetchSds();
      this.fetchCandle();
    },
    onSdsDate(date) {
      if (date === null) {
        this.noProductUfi = false;
        this.candle.product_ufi = '';
      }
    },
    onShow() {
      this.sds = null;
      this.fetchAll();
    },
    onHide(event) {
      if (this.preventClose && !confirm(this.preventMessage)) {
        event.preventDefault();
        return;
      }
      this.candle = null;
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
    roundNumber(value, minimumFractionDigits, maximumFractionDigits) {
      return roundNumber(value, minimumFractionDigits, maximumFractionDigits);
    },
    async copyPercentage(item) {
      item.copied = true;
      const text = roundNumber(item.pct, 5, 5);
      try {
        await navigator.clipboard.writeText(text);
        this.$toast.clear();
        this.$toast.success(`${item.title} ${text}% copied to clipboard`);
      } catch (err) {
        this.$toast.error(`Clipboard error: ${err}`);
      }
    },
    toggleNoProductUfi(checked) {
      if (checked) {
        this.candle.product_ufi = 'None';
      } else {
        this.candle.product_ufi = '';
      }
    },
  },
  watch: {
    candle: {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue === null) {
          return;
        }
        this.scheduleSaving();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.table tbody > tr {
  &.copied {
    background-color: #e3f3e3;
  }

  td {
    padding: 0;
  }
}
.total-percentage {
  padding: 0 12px;
}
</style>
